import { groupBy } from 'lodash-es'
import * as React from 'react'
import { Avatar } from 'src/design-system'
import { CollapsibleTable } from 'src/design-system/components/organisms/CollapsibleTable'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Position, Skill, Team } from '../types'
import { avatarFallback, formatCurrency as $ } from '../utils'
import { useWfiContext } from '../WfiContext'
import styles from './AnalysisCard.module.scss'

const HEADINGS = {
  role: 'Role',
  salary: 'Salary',
  topSkills: 'Top Skills',
}

type ProposalPreserveProps = {
  id: number
}

export const ProposalPreserve: React.VFC<ProposalPreserveProps> = ({ id }) => {
  const { analyses } = useWfiContext()

  const analysis = analyses.find((a) => a.id === id)

  if (analysis == null) return null

  // TODO: these calculations and transformations to get all the data for
  // users/teams/roles etc are duplicated between ProposalPreserve, ProposalGrow, and ProposalTransition.
  // they should be done in or exposed via memoized functions in `useWorkforceIntelligence`.
  // team ids get converted to strings in the `groupBy`,
  // since numbers cannot be object keys.
  const usersByTeam = new Map(
    Object.entries(groupBy(analysis.users, 'team_id'))
  )
  const teamsWithUsers = analysis.teams.map((team) => ({
    ...team,
    users: usersByTeam.get(team.id.toString()),
  }))

  const positions: Map<number, Position> = (() => {
    const map = new Map()
    const positions = analysis.teams.map((team) => team.positions).flat()
    for (const position of positions) {
      map.set(position.id, position)
    }
    return map
  })()
  const skills = (() => {
    const map: Map<number, Skill> = new Map()
    for (const skill of analysis.skills) {
      map.set(skill.id, skill)
    }
    return map
  })()

  const groupedRows = teamsWithUsers.map((team) => ({
    name: team.name,
    display: team.name,
    rows:
      team.users?.map((user) => {
        const position = positions.get(user.position_id)
        const maxRequirementLevel = Math.max(
          ...(position?.requirements ?? []).map(
            (requirement) => requirement.level
          )
        )
        const topSkills = (position?.requirements ?? [])
          .filter((req) => req.level === maxRequirementLevel)
          .map((req) => skills.get(req.skill_id)?.name)

        return {
          role: (
            <div className={styles.cellContainer}>
              <p>{position?.name ?? ''}</p>
              <p className={styles.name}>
                <Avatar
                  fallbackText={avatarFallback(user.name)}
                  size="xs"
                  src={user.avatar_url}
                />
                {user.name}
              </p>
            </div>
          ),
          salary: $(user.salary_usd),
          topSkills: <p className={styles.cellWrap}>{topSkills.join(', ')}</p>,
        }
      }) ?? [],
  }))

  return (
    <div>
      <CollapsibleTable headings={HEADINGS} groupedRows={groupedRows} />
    </div>
  )
}
